import { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";

interface CountryCode {
  code: string;
  dial: string;
  flag: string;
  name: string;
}

const COUNTRY_CODES: CountryCode[] = [
  { code: "KW", dial: "+965", flag: "🇰🇼", name: "Kuwait" },
  { code: "AE", dial: "+971", flag: "🇦🇪", name: "UAE" },
  { code: "SA", dial: "+966", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "BH", dial: "+973", flag: "🇧🇭", name: "Bahrain" },
  { code: "QA", dial: "+974", flag: "🇶🇦", name: "Qatar" },
  { code: "OM", dial: "+968", flag: "🇴🇲", name: "Oman" },
  { code: "IQ", dial: "+964", flag: "🇮🇶", name: "Iraq" },
  { code: "JO", dial: "+962", flag: "🇯🇴", name: "Jordan" },
  { code: "LB", dial: "+961", flag: "🇱🇧", name: "Lebanon" },
  { code: "EG", dial: "+20", flag: "🇪🇬", name: "Egypt" },
  { code: "US", dial: "+1", flag: "🇺🇸", name: "United States" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "IN", dial: "+91", flag: "🇮🇳", name: "India" },
  { code: "PK", dial: "+92", flag: "🇵🇰", name: "Pakistan" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "France" },
  { code: "TR", dial: "+90", flag: "🇹🇷", name: "Turkey" },
  { code: "CN", dial: "+86", flag: "🇨🇳", name: "China" },
  { code: "JP", dial: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "AU", dial: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "CA", dial: "+1", flag: "🇨🇦", name: "Canada" },
  { code: "BR", dial: "+55", flag: "🇧🇷", name: "Brazil" },
  { code: "MX", dial: "+52", flag: "🇲🇽", name: "Mexico" },
  { code: "ZA", dial: "+27", flag: "🇿🇦", name: "South Africa" },
  { code: "NG", dial: "+234", flag: "🇳🇬", name: "Nigeria" },
  { code: "KE", dial: "+254", flag: "🇰🇪", name: "Kenya" },
  { code: "MY", dial: "+60", flag: "🇲🇾", name: "Malaysia" },
  { code: "SG", dial: "+65", flag: "🇸🇬", name: "Singapore" },
  { code: "PH", dial: "+63", flag: "🇵🇭", name: "Philippines" },
  { code: "ID", dial: "+62", flag: "🇮🇩", name: "Indonesia" },
];

const DEFAULT_COUNTRY = COUNTRY_CODES.find((c) => c.code === "AE")!;

function parsePhoneValue(value: string): { country: CountryCode; number: string } {
  if (!value) return { country: DEFAULT_COUNTRY, number: "" };
  for (const c of COUNTRY_CODES) {
    if (value.startsWith(c.dial + " ")) {
      return { country: c, number: value.slice(c.dial.length + 1) };
    }
    if (value.startsWith(c.dial)) {
      return { country: c, number: value.slice(c.dial.length) };
    }
  }
  return { country: DEFAULT_COUNTRY, number: value };
}

async function detectCountryByIP(): Promise<string | null> {
  try {
    const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const data = await res.json();
    return data.country_code || null;
  } catch {
    return null;
  }
}

interface PhoneInputProps {
  value: string;
  onChange: (fullNumber: string) => void;
  placeholder?: string;
}

export function PhoneInput({ value, onChange, placeholder }: PhoneInputProps) {
  const parsed = parsePhoneValue(value);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(parsed.country);
  const [localNumber, setLocalNumber] = useState(parsed.number);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const geoDetected = useRef(false);

  // Auto-detect country via Geo IP on mount
  useEffect(() => {
    if (geoDetected.current || value) return;
    geoDetected.current = true;
    detectCountryByIP().then((code) => {
      if (code) {
        const country = COUNTRY_CODES.find((c) => c.code === code.toUpperCase());
        if (country) {
          setSelectedCountry(country);
          onChange(`${country.dial} ${localNumber}`);
        }
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus search when dropdown opens
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const filteredCountries = useMemo(() => {
    if (!search) return COUNTRY_CODES;
    const q = search.toLowerCase();
    return COUNTRY_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
    setOpen(false);
    setSearch("");
    onChange(`${country.dial} ${localNumber}`);
  };

  const handleNumberChange = (num: string) => {
    // Only allow digits, spaces, and dashes
    const cleaned = num.replace(/[^\d\s\-]/g, "");
    setLocalNumber(cleaned);
    onChange(`${selectedCountry.dial} ${cleaned}`);
  };

  return (
    <div className="flex gap-0" dir="ltr">
      {/* Country code selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex items-center gap-1.5 h-12 px-3 rounded-l-md border border-r-0 border-input bg-background",
            "hover:bg-muted/50 transition-colors text-sm font-medium whitespace-nowrap",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
        >
          <span className="text-lg leading-none">{selectedCountry.flag}</span>
          <span className="text-foreground">{selectedCountry.dial}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div className="absolute top-full left-0 z-50 mt-1 w-64 rounded-lg border border-border bg-background shadow-lg animate-in fade-in-0 zoom-in-95 duration-150">
            {/* Search */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search country..."
                  className="w-full h-8 pl-8 pr-3 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </div>
            {/* List */}
            <div className="max-h-52 overflow-y-auto py-1">
              {filteredCountries.length === 0 && (
                <p className="px-3 py-2 text-sm text-muted-foreground">No results</p>
              )}
              {filteredCountries.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => handleCountrySelect(c)}
                  className={cn(
                    "flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left",
                    selectedCountry.code === c.code && "bg-primary/5 text-primary"
                  )}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground text-xs">{c.dial}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Phone number input */}
      <Input
        type="tel"
        value={localNumber}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder={placeholder || "5XX XXX XXXX"}
        className="h-12 text-base rounded-l-none flex-1"
        dir="ltr"
      />
    </div>
  );
}
